import { useState, useEffect } from 'react';
import { Plus, Pencil, Loader2, X, Check, Search } from 'lucide-react';
import {
  AMBER, NAVY, BORDER_COLOR, BG_FIELD,
  bubbleStyle, fieldStyle, selectStyle, navyBtn, outlineBtn,
} from '../../styles/tokens';
import client from '../../api/client';
import { usePaginatedSort } from '../../shared/hooks/usePaginatedSort';
import PaginationBar from '../../shared/components/PaginationBar';
import SortableHeader from '../../shared/components/SortableHeader';

const CATEGORY_LABEL = { credit_card: 'Credit Card', petty_cash: 'Petty Cash' };
const CATEGORY_PILL = {
  credit_card: { bg: '#dbeafe', color: '#1e40af' },
  petty_cash:  { bg: '#fef3c7', color: '#92400e' },
};

export default function SageVendorManagement() {
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showAdd, setShowAdd]     = useState(false);
  const [newId, setNewId]         = useState('');
  const [newName, setNewName]     = useState('');
  const [newCategory, setNewCategory] = useState('credit_card');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [editRowId, setEditRowId] = useState(null);
  const [editVendorId, setEditVendorId] = useState('');
  const [editName, setEditName]   = useState('');
  const [editCategory, setEditCategory] = useState('credit_card');
  const [search, setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => { fetchItems(); }, []);

  async function fetchItems() {
    try {
      const res = await client.get('/api/admin/vendor-mappings');
      setItems(res.data);
    } catch (err) {
      console.error('Failed to load vendor mappings:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd() {
    if (!newId.trim() || !newName.trim()) return;
    setSaving(true);
    setError('');
    try {
      await client.post('/api/admin/vendor-mappings', {
        vendor_id: newId.trim(),
        vendor_name: newName.trim(),
        category: newCategory,
      });
      setNewId('');
      setNewName('');
      setNewCategory('credit_card');
      setShowAdd(false);
      await fetchItems();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al crear');
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate(id) {
    if (!editVendorId.trim() || !editName.trim()) return;
    try {
      await client.patch(`/api/admin/vendor-mappings/${id}`, {
        vendor_id: editVendorId.trim(),
        vendor_name: editName.trim(),
        category: editCategory,
      });
      setEditRowId(null);
      await fetchItems();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al actualizar');
    }
  }

  async function handleToggle(id) {
    try {
      await client.patch(`/api/admin/vendor-mappings/${id}/toggle`);
      await fetchItems();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al cambiar estado');
    }
  }

  const term = search.toLowerCase();
  const filteredItems = items.filter(i => {
    const matchesSearch =
      (i.vendor_id || '').toLowerCase().includes(term) ||
      (i.vendor_name || '').toLowerCase().includes(term);
    if (!matchesSearch) return false;
    if (categoryFilter !== 'all' && i.category !== categoryFilter) return false;
    if (statusFilter === 'active') return i.is_active;
    if (statusFilter === 'inactive') return !i.is_active;
    return true;
  });
  const activeCount = items.filter(i => i.is_active).length;

  // V29.2: hook MUST run on every render (before any early return) so hook order stays stable
  const {
    pageItems, page, pageSize, totalPages, totalItems,
    sortKey, sortDir, toggleSort, setPage, setPageSize,
  } = usePaginatedSort(filteredItems, { defaultSortKey: 'vendor_name' });

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
        <Loader2 size={20} className="spin" /> Cargando proveedores Sage…
      </div>
    );
  }

  return (
    <div className="page-pad" style={{ padding: '24px 28px', maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: '700', color: NAVY, margin: 0 }}>
          Proveedores Sage
          <span style={{ fontSize: 13, fontWeight: '400', color: '#94a3b8', marginLeft: 8 }}>
            ({activeCount} activos)
          </span>
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* V30.4: one-shot seed for fresh environments (e.g. after first prod deploy).
              Idempotent — re-running is safe; existing rows update vendor_name/category. */}
          {items.length === 0 && (
            <button
              onClick={async () => {
                if (!window.confirm('¿Importar el catálogo completo de proveedores Sage (Credit Card + Petty Cash)? Esto inicializa la lista de proveedores y solo está disponible cuando la lista está vacía.')) return;
                try {
                  const res = await client.post('/api/admin/vendor-mappings/seed');
                  alert(`Importado: ${res.data.counts?.credit_card || 0} credit_card, ${res.data.counts?.petty_cash || 0} petty_cash.`);
                  await fetchItems();
                } catch (err) {
                  alert(err.response?.data?.error || 'Error al importar.');
                }
              }}
              style={{ ...navyBtn, display: 'flex', alignItems: 'center', gap: 6, background: '#475569' }}
            >
              📥 Importar desde Vendor IDs.xlsx
            </button>
          )}
          <button onClick={() => { setShowAdd(true); setError(''); setNewId(''); setNewName(''); setNewCategory('credit_card'); }} style={{ ...navyBtn, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} /> Agregar Proveedor
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por Vendor ID o nombre…"
            style={{ ...fieldStyle, width: 320, paddingLeft: 30 }}
          />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...selectStyle, width: 'auto' }}>
          <option value="active">Activos</option>
          <option value="inactive">Inactivos</option>
          <option value="all">Todos</option>
        </select>
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} style={{ ...selectStyle, width: 'auto' }}>
          <option value="all">Todas las categorías</option>
          <option value="credit_card">Credit Card</option>
          <option value="petty_cash">Petty Cash</option>
        </select>
        <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 'auto' }}>
          {filteredItems.length} resultado{filteredItems.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Add form */}
      {showAdd && (
        <div style={{ ...bubbleStyle, marginBottom: 20, animation: 'fadeIn 0.2s ease' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: '0 0 160px' }}>
              <label style={labelStyle}>Vendor ID</label>
              <input
                value={newId}
                onChange={e => setNewId(e.target.value)}
                style={fieldStyle}
                placeholder="ej., HOMEDEPOT"
                autoFocus
              />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={labelStyle}>Nombre del Proveedor</label>
              <input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
                style={fieldStyle}
                placeholder="ej., Home Depot Inc."
              />
            </div>
            <div style={{ flex: '0 0 150px' }}>
              <label style={labelStyle}>Categoría</label>
              <select value={newCategory} onChange={e => setNewCategory(e.target.value)} style={{ ...selectStyle, width: '100%' }}>
                <option value="credit_card">Credit Card</option>
                <option value="petty_cash">Petty Cash</option>
              </select>
            </div>
            <button onClick={handleAdd} disabled={saving} style={{ ...navyBtn, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Agregando…' : 'Agregar'}
            </button>
            <button onClick={() => setShowAdd(false)} style={outlineBtn}>Cancelar</button>
          </div>
          {error && (
            <div style={{
              marginTop: 8, padding: '6px 12px', background: '#fef2f2', color: '#991b1b',
              border: '1px solid #fecaca', borderRadius: 6, fontSize: 12,
            }}>
              {error}
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div style={bubbleStyle}>
        {/* Table header */}
        <div style={{
          display: 'grid', gridTemplateColumns: '180px 1fr 130px 110px 90px',
          gap: 8, padding: '8px 0', borderBottom: `2px solid ${BORDER_COLOR}`,
          fontSize: 11, fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>
          <SortableHeader label="Vendor ID"   sortKey="vendor_id"   currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Nombre del Proveedor" sortKey="vendor_name" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Categoría"   sortKey="category"    currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Estado"      sortKey="is_active"   currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
          <span style={{ textAlign: 'right' }}>Acciones</span>
        </div>

        {/* Table rows */}
        {pageItems.map(item => {
          const pill = CATEGORY_PILL[item.category] || CATEGORY_PILL.credit_card;
          return (
          <div key={item.id} style={{
            display: 'grid', gridTemplateColumns: '180px 1fr 130px 110px 90px',
            gap: 8, alignItems: 'center',
            padding: '10px 0', borderBottom: `1px solid ${BORDER_COLOR}`,
            opacity: item.is_active ? 1 : 0.6,
          }}>
            {editRowId === item.id ? (
              <>
                <input
                  value={editVendorId}
                  onChange={e => setEditVendorId(e.target.value)}
                  style={{ ...fieldStyle, fontSize: 13 }}
                  autoFocus
                />
                <input
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleUpdate(item.id)}
                  style={{ ...fieldStyle, fontSize: 13 }}
                />
                <select
                  value={editCategory}
                  onChange={e => setEditCategory(e.target.value)}
                  style={{ ...selectStyle, fontSize: 12, width: '100%' }}
                >
                  <option value="credit_card">Credit Card</option>
                  <option value="petty_cash">Petty Cash</option>
                </select>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>—</span>
                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                  <button onClick={() => handleUpdate(item.id)} style={iconBtn} title="Guardar">
                    <Check size={14} style={{ color: '#16a34a' }} />
                  </button>
                  <button onClick={() => setEditRowId(null)} style={iconBtn} title="Cancelar">
                    <X size={14} />
                  </button>
                </div>
              </>
            ) : (
              <>
                <span style={{
                  fontSize: 13, fontWeight: '600', fontFamily: 'monospace',
                  color: item.is_active ? NAVY : '#94a3b8',
                  textDecoration: item.is_active ? 'none' : 'line-through',
                }}>{item.vendor_id}</span>
                <span style={{
                  fontSize: 14, fontWeight: '500',
                  color: item.is_active ? NAVY : '#94a3b8',
                  textDecoration: item.is_active ? 'none' : 'line-through',
                }}>{item.vendor_name}</span>
                <span>
                  <span style={{
                    display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                    fontSize: 11, fontWeight: '600',
                    background: pill.bg, color: pill.color,
                  }}>
                    {CATEGORY_LABEL[item.category] || item.category || '—'}
                  </span>
                </span>
                <span>
                  <span style={{
                    display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                    fontSize: 11, fontWeight: '600',
                    background: item.is_active ? '#f0fdf4' : '#fef2f2',
                    color: item.is_active ? '#166534' : '#991b1b',
                  }}>
                    {item.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                </span>
                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                  <button onClick={() => {
                    setEditRowId(item.id);
                    setEditVendorId(item.vendor_id || '');
                    setEditName(item.vendor_name || '');
                    setEditCategory(item.category || 'credit_card');
                  }} style={iconBtn} title="Editar">
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => handleToggle(item.id)} style={iconBtn} title={item.is_active ? 'Desactivar' : 'Reactivar'}>
                    {item.is_active
                      ? <X size={13} style={{ color: '#dc2626' }} />
                      : <Check size={13} style={{ color: '#16a34a' }} />}
                  </button>
                </div>
              </>
            )}
          </div>
          );
        })}
        {filteredItems.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            No se encontraron proveedores
          </div>
        )}
      </div>

      <PaginationBar
        page={page} pageSize={pageSize} totalPages={totalPages} totalItems={totalItems}
        onPageChange={setPage} onPageSizeChange={setPageSize}
      />
    </div>
  );
}

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: '600', color: '#475569', marginBottom: 4,
};

const iconBtn = {
  background: BG_FIELD, border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
  padding: '6px 8px', cursor: 'pointer', color: NAVY, display: 'inline-flex', alignItems: 'center',
};
